import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { Table } from '@/components/ui/Table';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/form/Input';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { listVoucherTypes, alterVoucherType, type AccVoucherType } from '@/api/accountsBooks';

interface Draft { vt: AccVoucherType; abbr: string; prefix: string; startNo: string; width: string }

const label = 'mb-1 block text-xs font-semibold text-[var(--text-secondary)]';

export function VoucherTypesPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState<Draft | null>(null);

  const query = useQuery({ queryKey: ['acc-books', 'voucher-types'], queryFn: listVoucherTypes });
  const rows = query.data ?? [];

  const saveMut = useMutation({
    mutationFn: (d: Draft) =>
      alterVoucherType(d.vt.id, { abbr: d.abbr.trim(), prefix: d.prefix.trim(), startNo: Number(d.startNo), width: Number(d.width) }),
    onSuccess: () => {
      toast.push('Voucher type saved', 'success');
      setDraft(null);
      qc.invalidateQueries({ queryKey: ['acc-books', 'voucher-types'] });
    },
    onError: (e) => toast.push(apiError(e, 'Save failed'), 'error'),
  });

  const preview = draft
    ? draft.prefix.trim() + String(Math.max(draft.vt.lastNo + 1, Number(draft.startNo) || 1)).padStart(Math.min(8, Math.max(1, Number(draft.width) || 1)), '0')
    : '';

  return (
    <div>
      <SectionHeader title="Voucher Types" />

      <Table<AccVoucherType>
        storageKey="acc-books-voucher-types"
        title="Voucher Types"
        count={`${rows.length} type${rows.length === 1 ? '' : 's'}`}
        rows={rows}
        rowKey={(r) => r.id}
        loading={query.isFetching && rows.length === 0}
        empty="No voucher types."
        columns={[
          { key: 'name', header: 'Name', minWidth: '140px', cell: (r) => <strong>{r.name}</strong> },
          { key: 'abbr', header: 'Abbreviation', width: '110px', cell: (r) => r.abbr },
          { key: 'prefix', header: 'Prefix', width: '120px', cell: (r) => <span className="font-mono">{r.prefix}</span> },
          { key: 'start', header: 'Starting no.', width: '100px', align: 'right', cell: (r) => r.startNo },
          { key: 'width', header: 'Width', width: '70px', align: 'right', cell: (r) => r.width },
          { key: 'next', header: 'Next number', width: '150px', cell: (r) => <span className="font-mono font-semibold">{r.nextNo}</span> },
          { key: 'vouchers', header: 'Vouchers', width: '90px', align: 'right', cell: (r) => r.vouchers },
          {
            key: 'actions', header: '', width: '90px', align: 'right', alwaysVisible: true,
            cell: (r) => (
              <Button variant="glass" size="sm" onClick={() => setDraft({ vt: r, abbr: r.abbr, prefix: r.prefix, startNo: String(r.startNo), width: String(r.width) })}>
                Alter
              </Button>
            ),
          },
        ]}
      />

      <Modal
        open={!!draft}
        onClose={() => setDraft(null)}
        title={draft ? `Alter Voucher Type ${draft.vt.name}` : ''}
        size="sm"
        footer={
          <>
            <Button variant="glass" onClick={() => setDraft(null)}>Cancel</Button>
            <Button variant="primary" disabled={saveMut.isPending} onClick={() => draft && saveMut.mutate(draft)}>
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        {draft && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={label}>Abbreviation</label>
                <Input value={draft.abbr} onChange={(e) => setDraft({ ...draft, abbr: e.target.value })} maxLength={12} />
              </div>
              <div>
                <label className={label}>Prefix</label>
                <Input value={draft.prefix} onChange={(e) => setDraft({ ...draft, prefix: e.target.value })} maxLength={20} />
              </div>
              <div>
                <label className={label}>Starting number</label>
                <Input type="number" min={1} step={1} value={draft.startNo} onChange={(e) => setDraft({ ...draft, startNo: e.target.value })} />
              </div>
              <div>
                <label className={label}>Width (1–8)</label>
                <Input type="number" min={1} max={8} step={1} value={draft.width} onChange={(e) => setDraft({ ...draft, width: e.target.value })} />
              </div>
            </div>
            <p className="text-xs text-[var(--text-secondary)]">
              Next number: <strong className="font-mono text-[var(--text-primary)]">{preview}</strong>
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}
